import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import Button from '@amzn/meridian/button';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Loader from '@amzn/meridian/loader';
import {
  CoreInput,
  CoreTextarea,
  CoreFilterableSelect,
  isNullOrWhitespace,
  useForm,
} from '@amzn/dots-core-ui';
import Alert from '@amzn/meridian/alert';
import { Plan, PlanType, useClient } from '../../api';

export type PlanFormProps = {
  plan?: Plan;
  planType?: PlanType;
  onSubmit: (plan: Partial<Plan>) => void;
  onCancel: () => void;
};

export const PlanForm = ({
  plan,
  planType,
  onSubmit,
  onCancel,
}: PlanFormProps): JSX.Element => {
  // input state
  const disablePlanType = plan !== undefined;
  const [type, setType] = useState<PlanType>(planType ?? 'static');
  const { tmsClient } = useClient();
  const validateSearchParams = useCallback(
    (value: unknown): string =>
      type === 'dynamic' && isNullOrWhitespace(value)
        ? 'Search Parameters are required'
        : '',
    [type]
  );

  const {
    values,
    errors,
    handlers,
    hasErrors,
    isSubmitting,
    handleSubmit,
    triggerValidation,
  } = useForm({
    name: {
      initialValue: plan?.name ?? '',
      validate: async (value): Promise<string> => {
        if (isNullOrWhitespace(value)) {
          return 'Name is required';
        }
        if (value === plan?.name) {
          return '';
        }
        const result = await tmsClient.plans.getAll({ name: value });
        if (result.count > 0) {
          return `${value} is already in use`;
        }
        return '';
      },
    },
    description: {
      initialValue: plan?.description ?? '',
    },
    owner: {
      initialValue: plan?.owner ?? '',
      validate: (value): string =>
        isNullOrWhitespace(value) ? 'Owner is required' : '',
    },
    searchParams: {
      initialValue: plan?.searchParams ?? '',
      validate: validateSearchParams,
    },
  });

  useEffect(() => {
    // todo: handle validation closure dependencies w/o useEffect here
    if (type === 'static') {
      handlers.searchParams('');
    }
    triggerValidation('searchParams');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const groups = useQuery('groups', async () => {
    const response = await tmsClient.groups.getAll();
    return response.content.map((item) => item.name);
  });

  if (groups.isLoading) {
    return (
      <Column
        minHeight="100%"
        alignmentHorizontal="center"
        alignmentVertical="center"
      >
        <Loader size="large" type="circular" />
      </Column>
    );
  }

  if (groups.isError) {
    return (
      <Alert type="error" size="xlarge" title="Whoops, something went wrong">
        error
      </Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Column spacingInset="200" alignmentHorizontal="stretch">
        {plan === undefined && (
          <Row alignmentHorizontal="center">
            <ButtonGroup value={type} onChange={setType} type="outline">
              <ButtonOption
                disabled={disablePlanType || isSubmitting}
                value="static"
              >
                Static
              </ButtonOption>
              <ButtonOption
                disabled={disablePlanType || isSubmitting}
                value="dynamic"
              >
                Dynamic
              </ButtonOption>
            </ButtonGroup>
          </Row>
        )}
        <CoreInput
          label="Name"
          value={values.name}
          errorMessage={errors.name}
          type="text"
          onChange={handlers.name}
          disabled={isSubmitting}
        />
        <CoreFilterableSelect
          label="Owner"
          value={values.owner}
          items={groups.data ?? []}
          errorMessage={errors.owner}
          onChange={handlers.owner}
          disabled={isSubmitting}
        />
        <CoreTextarea
          label="Description"
          value={values.description}
          errorMessage={errors.description}
          onChange={handlers.description}
          disabled={isSubmitting}
          rows={3}
        />
        {(plan === undefined || type === 'dynamic') && (
          <CoreTextarea
            label="Search Parameters"
            value={values.searchParams}
            errorMessage={errors.searchParams}
            onChange={handlers.searchParams}
            disabled={type === 'static' || isSubmitting}
            rows={3}
          />
        )}
        <Row
          alignmentHorizontal="end"
          alignmentVertical="center"
          widths="fit"
        >
          <Button type="secondary" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            disabled={hasErrors || isSubmitting}
            submit
          >
            {isSubmitting ? <Loader type="circular" size="small" /> : 'Confirm'}
          </Button>
        </Row>
      </Column>
    </form>
  );
};
